import React from 'react';
import { Link } from 'react-router-dom';
import { FaLightbulb, FaChartLine, FaMicrochip, FaArrowRight, FaExternalLinkAlt, FaUniversity, FaSolarPanel } from 'react-icons/fa';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Simple Hero Bar */}
      <section className="bg-gradient-to-r from-green-600 to-blue-600 py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full shadow-lg mb-8">
            <FaSolarPanel className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            About GEPredict
          </h1>
          <p className="text-xl md:text-2xl text-white opacity-90 max-w-4xl mx-auto">
            Novel optimized models to enhance performance forecasting of grid-connected PERC PV strings operating under semi-arid climate conditions
          </p>
        </div>
      </section>

      {/* Research Impact & Validation - Moved to Top */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl shadow-xl p-8 text-white">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold mb-4">Research Impact & Validation</h3>
              <p className="text-blue-100 text-lg max-w-4xl mx-auto">
                Our models have been rigorously validated using real measurements from a PERC PV string incorporating 
                12 JKM405M-72H PV modules at Green Energy Park research facility
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">25.0%</div>
                <div className="text-blue-100">PERC Cell Efficiency</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">&lt;4.16%</div>
                <div className="text-blue-100">NRMSE Accuracy</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">12</div>
                <div className="text-blue-100">JKM405M-72H Modules</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">843.1 GW</div>
                <div className="text-blue-100">Global PV Capacity</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Overview */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Research Foundation
            </h2>
            <p className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed">
              This study focuses on modeling and forecasting the performance of grid-connected photovoltaic (PV) strings, 
              aiming to enhance the accuracy of output power prediction, which is crucial for effective management and 
              stability of electrical grids in challenging semi-arid climate conditions.
            </p>
          </div>

          {/* Climate Context */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Climate Challenge in MENA Region</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The MENA region faces dramatic climate shifts with temperature rising by <strong>0.46°C per decade</strong> 
                  (significantly surpassing the global average increase of 0.18°C), making it among the driest areas globally 
                  and especially susceptible to climate change. The July 2023 heatwave exemplifies extreme temperature rises, 
                  leading to significant power outages and forest fires.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Rapid demographic growth in these regions further worsens the consequences of increased emissions, 
                  while shifting rainfall patterns have deepened the drought crisis in Morocco and the wider Mediterranean sub-region.
                </p>
              </div>
              <div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Clean energy investment rose by nearly <strong>50% from 2019 to 2023, hitting USD 1.8 trillion in 2023</strong>. 
                  However, deployment remains concentrated in developed countries, which contributed to 90% of the growth in 
                  wind and solar PV installations.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Solar photovoltaic (PV) technology plays a crucial role in this transition. Between 2019 and 2023, 
                  green technologies have averted around <strong>25 exajoules (EJ)</strong> of fossil fuel energy consumption per year.
                </p>
              </div>
            </div>
          </div>

          {/* Research Gap */}
          <div className="bg-blue-50 rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Addressing Critical Research Gaps</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              While previous research has extensively examined PV modules, there was a <strong>notable lack of studies specifically 
              targeting PV string behaviors</strong>, especially in harsh climates prevailing in semi-arid regions. Our research 
              addresses this critical gap by investigating the modeling and performance prediction of Passivated Emitter Rear Cell (PERC) 
              PV strings under challenging environmental conditions.
            </p>
            <p className="text-gray-700 leading-relaxed">
              PERC technology, developed in the 1980s by Martin Green, has evolved significantly. Modern PERC cells have reached 
              <strong>efficiencies of up to 25.0%</strong>, representing a major advancement in PV technology. However, integrating 
              PV solar energy into electrical grids poses significant challenges due to production unpredictability, which introduces 
              variability and complicates synchronization between electricity generation and consumption.
            </p>
          </div>
        </div>
      </section>

      {/* Three Models Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Advanced Mathematical Models
            </h2>
            <p className="text-xl text-gray-600 max-w-5xl mx-auto">
              Novel analytical and predictive approaches based on both implicit and explicit models, 
              specifically optimized for grid-connected PERC PV string performance forecasting
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* SDM Model */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200 hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLightbulb className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">SDM</h3>
                <p className="text-blue-600 font-semibold text-sm">Single Diode Model</p>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Implicit model with five physical parameters: photocurrent, saturation current, ideality factor, 
                series resistance, and parallel resistance for comprehensive PV analysis.
              </p>
            </div>

            {/* DM Model */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200 hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaChartLine className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">DM</h3>
                <p className="text-green-600 font-semibold text-sm">Das Model</p>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Explicit model with novel mathematical formulations for shape parameters as functions of 
                temperature and irradiance, optimized for computational efficiency.
              </p>
            </div>

            {/* BM Model */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200 hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaMicrochip className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">BM</h3>
                <p className="text-purple-600 font-semibold text-sm">Boutana et al. Model</p>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Advanced explicit model with enhanced shape parameter optimization, 
                providing high accuracy while maintaining computational simplicity.
              </p>
            </div>

            {/* PLM Model */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border border-orange-200 hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaArrowRight className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">PLM</h3>
                <p className="text-orange-600 font-semibold text-sm">Power Law Model</p>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Simplified analytical approach with fewer parameters, offering precision 
                comparable to complex implicit models with enhanced computational speed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Publications Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Research Publications
              </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Published in the prestigious <strong>Solar Energy</strong> journal, our research represents 
              significant breakthroughs in photovoltaic performance modeling
            </p>
          </div>

          <div className="space-y-8">
            {/* First Publication */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Novel optimized models to enhance performance forecasting of grid-connected PERC PV string operating under semi-arid climate conditions
                  </h3>
                  <p className="text-blue-700 font-medium mb-4">
                    Author: Khadija El Ainaoui
                  </p>
                </div>
                <a 
                  href="https://www.sciencedirect.com/science/article/abs/pii/S0038092X24006716" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="ml-4 text-blue-600 hover:text-blue-700 flex-shrink-0"
                >
                  <FaExternalLinkAlt className="w-6 h-6" />
                </a>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-xl mb-6">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Research Highlights:</h4>
                <div className="space-y-4">
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Comprehensive Performance Investigation:</strong> Conducted an in-depth investigation for modeling and 
                    forecasting grid-connected PERC PV string performance, addressing critical gaps in understanding string-level 
                    behaviors under challenging environmental conditions.
                  </p>
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Advanced Mathematical Innovation:</strong> Introduced new mathematical models for DM and BM shape parameters 
                    as explicit functions of temperature and irradiance, enabling more accurate and computationally efficient predictions 
                    for semi-arid climate conditions.
                  </p>
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Novel Analytical Formulations:</strong> Derived innovative analytical formulas relating DM shape parameters 
                    to key PV metrics, providing unprecedented insights into PV string behavior and performance optimization.
                  </p>
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Validated Real-World Implementation:</strong> Extensively validated using real-world data from a grid-connected 
                    PERC PV string serving at Green Energy Park research facility in Benguerir, Morocco, demonstrating strong agreement 
                    between predicted and measured PV performance.
                  </p>
                </div>
              </div>
            </div>

            {/* Second Publication */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    New analytical expressions of output current for multi-exponential model of PV solar cells and modules based on Taylor series expansion
                  </h3>
                  <p className="text-green-700 font-medium mb-4">
                    Author: Khadija El Ainaoui
                  </p>
                </div>
                <a 
                  href="https://www.sciencedirect.com/science/article/abs/pii/S0038092X24006480#:~:text=In%20this%20paper,%20novel%20analytical%20expressions%20of%20output%20current%20for" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="ml-4 text-green-600 hover:text-green-700 flex-shrink-0"
                >
                  <FaExternalLinkAlt className="w-6 h-6" />
                </a>
              </div>
              
              <div className="bg-green-50 p-6 rounded-xl mb-6">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Research Highlights:</h4>
                <div className="space-y-4">
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Breakthrough Analytical Expressions:</strong> Developed unprecedented analytical expressions for photovoltaic 
                    module output current applicable to both single-diode and multi-diode models, solving long-standing challenges in 
                    analytical handling of exponential functions in PV modeling.
                  </p>
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Revolutionary Parameter Extraction:</strong> Proposed a novel approach for extracting multi-diode model 
                    physical parameters, significantly improving efficiency compared to traditional iterative methods requiring 
                    initial value estimations and long computing times.
                  </p>
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Rigorous Validation Framework:</strong> Performed comprehensive benchmarking of current values generated 
                    by novel expressions against exact numerical solutions, demonstrating superior precision with reduced Absolute 
                    Error (AE) and Root Mean Square Error (RMSE) values.
                  </p>
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Taylor Series Innovation (TSEC):</strong> Successfully developed Taylor Series Expressions of Current that 
                    enable efficient parameter search and accurate I-V data generation, representing a paradigm shift in PV performance 
                    modeling methodology for both single and multi-exponential models.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Institution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaUniversity className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Research Institution</h2>
          <p className="text-xl font-semibold text-gray-800 mb-4">
            Green Energy Park (UM6P/IRESEN)
          </p>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Mohammed VI Polytechnic University<br />
            Institute for Research in Solar Energy and New Energies<br />
            Benguerir, Morocco
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
